/**
 * An approval chain in the spec and the draft (L55 of docs/plans/ledger.md,
 * step 12.1: setup writes approval chains from words). "Expenses above
 * ₹5,000 go to the manager, and above ₹50,000 to me as well" is a chain on
 * the Expenses record: which entries need approval, then its steps in order.
 * Apply saves it as the record's live chain (`approvals/store.ts` saveChain),
 * the same chain the record's Approvals settings and OS's chain card write.
 *
 * A setup knows no one yet but the owner, so a step it writes is decided by
 * the writer's manager (Reports to, climbing to the owner) or by the owner or
 * an admin. A role or a named person is set once the team is in, in the
 * record's settings or through OS.
 *
 * `approvalChainProblems` refuses a chain apply could not save. Pure.
 */
import { APPROVAL_STEPS_MAX, type ApprovalConditions } from '../api';
import type { RuleProblem, RuleRecord } from './posting';

/** Who decides a step a setup writes: the writer's manager, or the owner or an admin. */
export type SpecApprovalWho = { readonly kind: 'manager' } | { readonly kind: 'owner_or_admin' };

export type SpecApprovalStep = {
  readonly who: SpecApprovalWho;
  /** The step applies only to an entry above this amount. Null: every entry the chain catches. */
  readonly above: number | null;
};

export type SpecApprovalChain = {
  readonly conditions: ApprovalConditions;
  readonly steps: readonly SpecApprovalStep[];
};

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isAmount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

/** A chain read off the wire (an op, a composed setup), or a sentence saying what is wrong with its shape. */
export const readSpecApprovalChain = (raw: unknown): SpecApprovalChain | string => {
  if (!isObject(raw)) return 'an object with conditions and steps';
  const conditions = raw.conditions;
  if (!isObject(conditions) || typeof conditions.every !== 'boolean' || typeof conditions.byRole !== 'boolean') return 'conditions as { every, above, byRole }';
  if (conditions.above !== null && conditions.above !== undefined && !isAmount(conditions.above)) return 'conditions.above as an amount above zero, or null';
  if (!Array.isArray(raw.steps) || raw.steps.length < 1 || raw.steps.length > APPROVAL_STEPS_MAX) return `steps as one to ${APPROVAL_STEPS_MAX} steps`;
  const steps: SpecApprovalStep[] = [];
  for (const [index, one] of raw.steps.entries()) {
    if (!isObject(one) || !isObject(one.who)) return `steps[${index}] as { who, above }`;
    const kind = one.who.kind;
    if (kind !== 'manager' && kind !== 'owner_or_admin') return `steps[${index}].who as the manager or the owner or an admin`;
    if (one.above !== null && one.above !== undefined && !isAmount(one.above)) return `steps[${index}].above as an amount above zero, or null`;
    steps.push({ who: { kind }, above: isAmount(one.above) ? one.above : null });
  }
  return {
    conditions: { every: conditions.every, above: isAmount(conditions.above) ? conditions.above : null, byRole: conditions.byRole },
    steps,
  };
};

/**
 * Every problem with a record's chain, or none: a chain sits on a record whose
 * entries people write, catches some entries, and an amount it names is one
 * the record's entries hold.
 */
export const approvalChainProblems = (chain: SpecApprovalChain, record: RuleRecord, at: string): RuleProblem[] => {
  const problems: RuleProblem[] = [];
  const say = (code: string, message: string): void => void problems.push({ code, message, path: at });
  if (record.kind !== 'record') say('APPROVAL_CHAIN_ON_LIST', `"${record.key}" is a list; only a record's entries wait for approval.`);
  const { every, above, byRole } = chain.conditions;
  if (!every && above === null && !byRole) say('APPROVAL_CHAIN_SHAPE', 'The chain catches no entry: say every entry, entries above an amount, or entries by people who need approval.');
  if (chain.steps.length < 1 || chain.steps.length > APPROVAL_STEPS_MAX) say('APPROVAL_CHAIN_SHAPE', `A chain has one to ${APPROVAL_STEPS_MAX} steps.`);
  const amounts = above !== null || chain.steps.some((step) => step.above !== null);
  if (amounts && !record.fields.some((field) => field.type === 'money' || field.type === 'number')) {
    say('APPROVAL_CHAIN_SHAPE', `The chain names an amount, and "${record.key}" holds none.`);
  }
  return problems;
};
